import { useEffect, useState } from "react";
import type { FormEvent } from "react";

import type { AuthenticatedShellSection } from "../../../components/authenticated/AuthenticatedAppShell";
import type { ApiClient } from "../../../infrastructure/http/apiClient";
import {
  buildBookingPolicyForm,
  loadBookingPolicy,
  updateBookingPolicy,
  type BookingPolicyForm,
  type UpdateBookingPolicyInput
} from "../../settings";
import { toBookingPolicyError } from "../domain/errors";
import type { AuthenticatedShellPageProps, BookingPolicyState, ShellState } from "../domain/shellTypes";

export function useBookingPolicySurface({
  activeSection,
  apiClient,
  initialBookingPolicy,
  initialBookingPolicyError,
  isProfessionalUser,
  isStaffUser,
  sessionToken,
  state
}: Pick<AuthenticatedShellPageProps, "initialBookingPolicy" | "initialBookingPolicyError"> & {
  activeSection: AuthenticatedShellSection;
  apiClient: ApiClient;
  isProfessionalUser: boolean;
  isStaffUser: boolean;
  sessionToken: string;
  state: ShellState;
}) {
  const [bookingPolicyState, setBookingPolicyState] = useState<BookingPolicyState>(() => {
    if (initialBookingPolicy) {
      return { status: "success", policy: initialBookingPolicy, saved: false };
    }

    if (initialBookingPolicyError) {
      return { status: "error", title: initialBookingPolicyError.title, message: initialBookingPolicyError.message, retryable: true };
    }

    return { status: "idle" };
  });
  const [bookingPolicyForm, setBookingPolicyForm] = useState<BookingPolicyForm>(() => buildBookingPolicyForm(initialBookingPolicy));

  useEffect(() => {
    if (activeSection !== "settings" || state.status !== "success" || isProfessionalUser || isStaffUser || !sessionToken || bookingPolicyState.status !== "idle") {
      return;
    }

    setBookingPolicyState({ status: "loading" });
    loadBookingPolicy(apiClient)
      .then((policy) => {
        setBookingPolicyForm(buildBookingPolicyForm(policy));
        setBookingPolicyState({ status: "success", policy, saved: false });
      })
      .catch((error) => {
        setBookingPolicyState(toBookingPolicyError(error));
      });
  }, [activeSection, apiClient, bookingPolicyState.status, isProfessionalUser, isStaffUser, sessionToken, state.status]);

  async function handleBookingPolicySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const currentPolicy = bookingPolicyState.status === "success" || bookingPolicyState.status === "saving" || bookingPolicyState.status === "error"
      ? bookingPolicyState.policy
      : undefined;
    const confirmationDeadlineMinutes = Number(bookingPolicyForm.confirmationDeadlineMinutes);

    if (!currentPolicy) {
      setBookingPolicyState({ status: "error", title: "Politica indisponivel", message: "Carregue a configuracao antes de salvar.", retryable: false });
      return;
    }

    if (!Number.isFinite(confirmationDeadlineMinutes) || confirmationDeadlineMinutes < 0) {
      setBookingPolicyState({ status: "error", title: "Revise o prazo", message: "Informe o prazo de confirmacao em minutos.", retryable: false, policy: currentPolicy });
      return;
    }

    const input: UpdateBookingPolicyInput = {
      booking_resource_selection_mode: bookingPolicyForm.bookingResourceSelectionMode,
      customer_capture_mode: bookingPolicyForm.customerCaptureMode,
      extended_customer_fields: bookingPolicyForm.customerCaptureMode === "extended" ? bookingPolicyForm.extendedCustomerFields : [],
      presence_confirmation: bookingPolicyForm.presenceConfirmation,
      auto_release_unconfirmed: bookingPolicyForm.presenceConfirmation === "required" && bookingPolicyForm.autoReleaseUnconfirmed,
      confirmation_deadline_minutes: confirmationDeadlineMinutes,
      arrival_checkpoint: bookingPolicyForm.arrivalCheckpoint
    };

    setBookingPolicyState({ status: "saving", policy: currentPolicy });

    try {
      const policy = await updateBookingPolicy(apiClient, input);
      setBookingPolicyForm(buildBookingPolicyForm(policy));
      setBookingPolicyState({ status: "success", policy, saved: true });
    } catch (error) {
      setBookingPolicyState(toBookingPolicyError(error, currentPolicy));
    }
  }

  function resetBookingPolicy() {
    setBookingPolicyState({ status: "idle" });
    setBookingPolicyForm(buildBookingPolicyForm(undefined));
  }

  return {
    bookingPolicyForm,
    bookingPolicyState,
    handleBookingPolicySubmit,
    resetBookingPolicy,
    setBookingPolicyForm
  };
}
